/**
 * Intercom Call State Store
 * 
 * Manages two-way audio call state for intercom communication.
 * Coordinates between audio capture service, UI controls, and notification system.
 */
import { create } from 'zustand';
import audioCaptureService from '../services/audioCaptureService';

const useIntercomCallStore = create((set, get) => ({
  // Call state
  callState: 'idle', // idle | ringing | connecting | active | ended | error
  activeIntercomId: null,
  activeCameraId: null,
  activeIntercomName: null,
  
  // Audio controls
  isMuted: false,
  isPTTActive: true,
  pttMode: false, // true = push-to-talk, false = open mic
  audioLevel: 0, // 0-1 for visualization
  
  // Call timing
  callStartTime: null,
  callDuration: 0,
  _durationInterval: null,
  
  // Connection info
  connectionState: 'idle', // idle | connecting | connected | disconnected | error
  errorMessage: null,

  /**
   * Answer an incoming intercom call
   */
  answerCall: async (intercomId, cameraId, intercomName, tenantId) => {
    const state = get();
    
    // Prevent answering if already in a call
    if (state.callState === 'active' || state.callState === 'connecting') {
      console.warn('[CallStore] Already in a call');
      return false;
    }

    set({
      callState: 'connecting',
      activeIntercomId: intercomId,
      activeCameraId: cameraId,
      activeIntercomName: intercomName || 'Intercom',
      connectionState: 'connecting',
      errorMessage: null,
      isMuted: false,
      isPTTActive: true,
      audioLevel: 0
    });

    // Setup audio capture callbacks
    audioCaptureService.onAudioLevel = (level) => {
      set({ audioLevel: level });
    };

    audioCaptureService.onStateChange = (connectionState) => {
      set({ connectionState });
    };

    try {
      // Start the audio call (mic access + audio pipeline + WebSocket)
      await audioCaptureService.startCall(cameraId, tenantId);

      // Call connected - start duration timer
      const startTime = Date.now();
      const interval = setInterval(() => {
        set({ callDuration: Math.floor((Date.now() - startTime) / 1000) });
      }, 1000);

      set({
        callState: 'active',
        callStartTime: startTime,
        _durationInterval: interval
      });

      // Request wake lock to keep screen on during call
      try {
        if ('wakeLock' in navigator) {
          await navigator.wakeLock.request('screen');
        }
      } catch (e) {
        // Wake lock not supported or denied - not critical
      }

      console.log('[CallStore] Call active');
      return true;

    } catch (err) {
      console.error('[CallStore] Failed to answer call:', err);
      set({
        callState: 'error',
        errorMessage: err.message,
        connectionState: 'error'
      });
      audioCaptureService.cleanup();
      return false;
    }
  },

  /**
   * End the current call
   */
  endCall: () => {
    const state = get();
    
    // Stop duration timer
    if (state._durationInterval) {
      clearInterval(state._durationInterval);
    }

    // Cleanup audio resources
    audioCaptureService.cleanup();

    set({
      callState: 'idle',
      activeIntercomId: null,
      activeCameraId: null,
      activeIntercomName: null,
      isMuted: false,
      isPTTActive: false,
      audioLevel: 0,
      callStartTime: null,
      callDuration: 0,
      _durationInterval: null,
      connectionState: 'idle',
      errorMessage: null
    });

    console.log('[CallStore] Call ended');
  },

  /**
   * Toggle microphone mute
   */
  toggleMute: () => {
    const state = get();
    const newMuted = !state.isMuted;
    
    // Mute/unmute the actual media stream tracks
    if (audioCaptureService.stream) {
      audioCaptureService.stream.getAudioTracks().forEach(track => {
        track.enabled = !newMuted;
      });
    }

    set({ isMuted: newMuted });
  },

  /**
   * Start push-to-talk (button pressed)
   */
  startPTT: () => {
    audioCaptureService.startPTT();
    set({ isPTTActive: true });
  },

  /**
   * Stop push-to-talk (button released)
   */
  stopPTT: () => {
    audioCaptureService.stopPTT();
    set({ isPTTActive: false });
  },

  /**
   * Toggle PTT / Open Mic mode
   */
  togglePTTMode: () => {
    const newPttMode = audioCaptureService.togglePTTMode();
    set({ 
      pttMode: newPttMode,
      isPTTActive: !newPttMode // Open mic = always active
    });
  },

  /**
   * Set an incoming call as ringing (notification received)
   */
  setRinging: (intercomId, cameraId, intercomName) => {
    set({
      callState: 'ringing',
      activeIntercomId: intercomId,
      activeCameraId: cameraId,
      activeIntercomName: intercomName || 'Intercom'
    });
  },

  /**
   * Dismiss a ringing call without answering
   */
  dismissCall: () => {
    set({
      callState: 'idle',
      activeIntercomId: null,
      activeCameraId: null,
      activeIntercomName: null
    });
  },

  /**
   * Format call duration as MM:SS
   */
  getFormattedDuration: () => {
    const duration = get().callDuration;
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}));

export default useIntercomCallStore;
